import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { AxiosError } from 'axios'
import {
    AccountInfo,
    AuthenticationResult,
    BrowserCacheLocation,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser'
import { useMount } from '@fluentui/react-hooks'
import { asBoolean } from '../helpers/functionHelper'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import { DateTime } from 'luxon'
import useAxios from '../hooks/useAxios'
import { ServerAuthorizationTokenResponse } from '@azure/msal-common/dist/response/ServerAuthorizationTokenResponse'
import { Role } from '../hooks/useRoles'
import { Destination, graphBaseUrl } from './AxiosContext'
import useLocalStorage from '../hooks/useLocalStorage'

export const AUTHENTICATION_SCOPES: string[] = [`${process.env.BOOTSTRAP_API_URI}/access_as_user`, 'openid', 'email', 'profile']

export type AzureTokenPayload = OfficeTokenPayload & {
    roles: Role[]
}

export type OfficeTokenPayload = {
    oid: string // UUID of user
    name: string // User's display name
    preferred_username: string // E-mail address of user
    unique_name: string // E-mail address of user
    upn: string // unified name, most often the e-mail address
    scp: string // Space-separated scopes
    tid: string // Tenant ID
    uti: string // Unique, per-token identifier that is case-sensitive.
} & JwtPayload

export type Host = {
    RowKey: string
    Name: string
    Backend: string
    ClientId: string
    ClientPath: string
    DriveId: string
    ReplicationMail: string
    tokens: Tokens
}

export enum SecurityAuthority {
    Hammock = 'hammock', // Connect to HAMMOCK in the user's tenant.
    SharePoint = 'sharepoint', // Connect to MS Graph in the Hammock.nl tenant.
    Exchange = 'exchange', // Connect to MS Graph in the user's tenant.
    WebPubSub = 'webpubsub', // Connect to A Web Pub Sub socket in the user's hub.
}

export const DestinationAuthorityMap = new Map<Destination, SecurityAuthority>([
    [Destination.Hammock, SecurityAuthority.Hammock],
    [Destination.Exchange, SecurityAuthority.Exchange],
    [Destination.ReplicationEmail, SecurityAuthority.Exchange],
    [Destination.Email, SecurityAuthority.Exchange],
    [Destination.PrivateEmail, SecurityAuthority.Exchange],
    [Destination.SharePoint, SecurityAuthority.SharePoint],
    [Destination.Documents, SecurityAuthority.SharePoint],
])

type PartialServerAuthorizationTokenResponse = Pick<ServerAuthorizationTokenResponse, 'access_token' | 'refresh_token' | 'token_type'>

export type WebPubSubToken = PartialServerAuthorizationTokenResponse & {
    hub: string
    url: string
    issued_at: string
    expires_at: string
}

export type Tokens = {
    [SecurityAuthority.Hammock]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.SharePoint]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.Exchange]?: ServerAuthorizationTokenResponse
    [SecurityAuthority.WebPubSub]?: WebPubSubToken
}

export type AuthorizationContextType = {
    identified: boolean
    authenticated: boolean
    user?: AccountInfo
    mailbox?: string
    bootstrapToken?: string
    hosts?: Host[]
    host?: Host
    setHost: (host: Host) => void
    tokens?: Tokens
    roles?: Role[]
    updateTokens: (authority: SecurityAuthority) => Promise<ServerAuthorizationTokenResponse | WebPubSubToken>
    getBaseUrl: (destination: Destination) => string | undefined
    reset: () => void
}

const AuthorizationContext = createContext<AuthorizationContextType>({
    identified: false,
    authenticated: false,
    user: undefined,
    mailbox: undefined,
    bootstrapToken: undefined,
    host: undefined,
    setHost: undefined,
    tokens: undefined,
    roles: undefined,
    updateTokens: () => Promise.resolve<ServerAuthorizationTokenResponse | WebPubSubToken>(undefined),
    getBaseUrl: () => graphBaseUrl,
    reset: () => {},
})

type AuthenticationStorageDto = Pick<AuthorizationContextType, 'identified' | 'authenticated' | 'user' | 'mailbox'>
type AuthorizationHostStorageDto = Pick<AuthorizationContextType, 'bootstrapToken' | 'hosts' | 'host'>
type AuthorizationTokensStorageDto = Pick<AuthorizationContextType, 'tokens' | 'roles'>

function checkSharedMailboxEmail(setMailbox: (mailbox: string) => void) {
    if (isOfficeInitialized() && Office.context.mailbox.item?.getSharedPropertiesAsync) {
        Office.context.mailbox.item?.getSharedPropertiesAsync?.(result => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                console.debug(
                    'checkSharedMailboxEmail: Error while detecting shared mailbox:',
                    result.error.code,
                    result.error.name,
                    result.error.message,
                )
                setMailbox(Office.context.mailbox.userProfile.emailAddress)
                return
            }
            console.debug(`checkSharedMailboxEmail: Shared mailbox detected: ${JSON.stringify(result.value)}`)
            setMailbox(result.value.targetMailbox)
        })
    } else if (isOfficeInitialized()) {
        console.debug(
            "checkSharedMailboxEmail: No message selected or the message isn't part of a shared mailbox:",
            Office.context.mailbox.userProfile.emailAddress,
        )
        setMailbox(Office.context.mailbox.userProfile.emailAddress)
    }
}

const parseBootstrapToken = (
    token: string,
    user: AccountInfo,
    setUser: (token: AccountInfo) => void,
    bootstrapTokenRef: React.MutableRefObject<string | undefined>,
    setBootstrapTokenExpiresAt: (expires: DateTime) => void,
) => {
    let decodedToken = undefined
    try {
        decodedToken = token && jwtDecode<OfficeTokenPayload>(token)
    } catch (e) {
        console.error(e)
    }
    console.debug(
        DateTime.now().toUTC().toISO(),
        'AuthorizationProvider: parseBootstrapToken: using token:',
        decodedToken?.uti,
        '. Expires at ',
        decodedToken && DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' }).toISO(),
    )

    bootstrapTokenRef.current = token
    setBootstrapTokenExpiresAt(decodedToken ? DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' }) : DateTime.now().plus({ minute: 1 }).toUTC())

    if (isOfficeInitialized() && decodedToken && user?.localAccountId !== decodedToken.oid) {
        setUser({
            homeAccountId: `${decodedToken.oid}.${decodedToken.tid}`,
            environment: 'login.windows.net',
            tenantId: decodedToken.tid,
            username: decodedToken.preferred_username?.toLowerCase() || decodedToken.unique_name?.toLowerCase() || decodedToken.upn?.toLowerCase(),
            localAccountId: decodedToken.oid,
            name: decodedToken.name,
        })
    }
}

const getTokenExpiryDateTime = (response: PartialServerAuthorizationTokenResponse): DateTime => {
    const token = response && jwtDecode<OfficeTokenPayload>(response.access_token)
    if (token?.exp) {
        return DateTime.fromSeconds(token?.exp, { zone: 'UTC' })
    } else if (token) {
        console.warn('no expiry found in token', token?.uti)
    }
    return DateTime.now().toUTC()
}

const isTokenNotExpired = (response: PartialServerAuthorizationTokenResponse) => {
    const exp = getTokenExpiryDateTime(response)
    const now = DateTime.now().toUTC()
    return exp.toMillis() - now.toMillis() - 10_000 > 0
}

const isTransientNetworkError = (error: unknown): boolean => {
    const axiosError = error as AxiosError & { cause?: { code?: string; message?: string } }
    const combinedErrorText = [axiosError?.code, axiosError?.name, axiosError?.message, axiosError?.cause?.code, axiosError?.cause?.message]
        .filter(Boolean)
        .join(' ')
        .toUpperCase()

    return combinedErrorText.includes('ERR_NETWORK') || combinedErrorText.includes('NETWORK ERROR')
}

/**
 * <p>This context is responsible for detecting the current user, the current mailbox and fetching a bootstrap token.</p>
 * <p>There are 2 situations:
 * - Standalone: The current mailbox is always the user's mailbox. No mailbox switch detection is performed.
 * - Outlook: The current mailbox is either the user's mailbox or a shared mailbox (info@ or support@).
 *   - Outlook will inform us of the current mailbox's e-mail address.
 *   - Mailbox switch detection is added so Outlook will inform us of the current mailbox's e-mail address when switching between e-mail messages.
 *
 * </p>
 */
export function AuthorizationProvider(props: PropsWithChildren): ReactElement {
    const axios = useAxios()

    /**
     * The client responsible for communicating with MS to gain a bootstrapToken.
     */
    const [pca, setPca] = useState<IPublicClientApplication>()
    const [isPCAReady, setIsPCAReady] = useState<boolean>(false)

    /**
     * A list of variables required to login / refresh tokens.
     */
    const [user, setUser] = useState<AccountInfo>()
    const [mailbox, setMailbox] = useState<string>()

    const bootstrapTokenRef = useRef<string>()
    const bootstrapTokenRequestRef = useRef<Promise<void>>()
    const [bootstrapTokenExpiresAt, setBootstrapTokenExpiresAt] = useState<DateTime>()
    const bootstrapTokenTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    const [hosts, setHosts] = useState<Host[]>()
    const [host, setHost] = useState<Host>()
    const hostsRequestRef = useRef<Promise<void>>()

    const [tokens, setTokens] = useState<Tokens>()
    const tokensRequestRef = useRef<Promise<void | Tokens>>()
    const [tokensExpiresAt, setTokensExpiresAt] = useState<DateTime>()
    const tokensTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    const bootstrapTokenValid = useMemo(() => {
        if (!bootstrapTokenRef.current) {
            return false
        }
        let decodedToken = undefined
        try {
            decodedToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        if (decodedToken && decodedToken.exp) {
            const exp = DateTime.fromSeconds(decodedToken.exp, { zone: 'UTC' })
            const now = DateTime.now().toUTC()
            return exp.toMillis() - now.toMillis() > 1_000
        }

        return false
    }, [bootstrapTokenExpiresAt])

    const identified = useMemo(() => !!user && bootstrapTokenValid, [bootstrapTokenValid, user])

    const authenticated = useMemo(
        () => !!host && tokens?.[SecurityAuthority.WebPubSub] && isTokenNotExpired(tokens?.[SecurityAuthority.WebPubSub]),
        [tokens],
    )

    const roles = useMemo<Role[]>(
        () =>
            tokens?.[SecurityAuthority.Hammock] ? jwtDecode<AzureTokenPayload>(tokens?.[SecurityAuthority.Hammock].access_token)?.roles || [] : [],
        [tokens],
    )

    const { loaded: loadedFirstVisit, values: firstVisit } = useLocalStorage<boolean, boolean>({
        key: 'firstVisit',
        defaultValue: true,
        fromJson: values => values,
        toJson: values => values,
    })

    const {
        values: authenticationStorage,
        setValues: setAuthenticationStorage,
        loaded: authenticationStorageLoaded,
    } = useLocalStorage<AuthenticationStorageDto, AuthenticationStorageDto>({
        key: 'AuthenticationStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
    })
    const [isUserHydrated, setIsUserHydrated] = useState<boolean>(false)

    const storagePrefix = useMemo<string>(
        () =>
            !authenticationStorageLoaded
                ? undefined
                : [window.location.pathname.replaceAll('/', ''), authenticationStorage?.user?.homeAccountId].filter(Boolean).join('-'),
        [authenticationStorageLoaded, authenticationStorage],
    )
    const {
        values: authorizationHostStorage,
        setValues: setAuthorizationHostStorage,
        loaded: authorizationHostStorageLoaded,
    } = useLocalStorage<AuthorizationHostStorageDto, AuthorizationHostStorageDto>({
        hostKey: storagePrefix,
        key: 'AuthorizationHostStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        load: authenticationStorageLoaded && storagePrefix !== undefined,
    })
    const [isBootstrapHydrated, setIsBootstrapHydrated] = useState<boolean>(false)

    const tokensStoragePrefix = useMemo<string>(
        () => (!host ? undefined : [host?.RowKey, storagePrefix].filter(Boolean).join('-')),
        [host, storagePrefix],
    )
    const {
        values: authorizationTokensStorage,
        setValues: setAuthorizationTokensStorage,
        loaded: authorizationTokensStorageLoaded,
    } = useLocalStorage<AuthorizationTokensStorageDto, AuthorizationTokensStorageDto>({
        hostKey: tokensStoragePrefix,
        key: 'AuthorizationTokensStorage',
        fromJson: values => values,
        toJson: values => values,
        defaultValue: undefined,
        load: authorizationHostStorageLoaded && tokensStoragePrefix !== undefined,
    })
    const [isHydrated, setIsHydrated] = useState<boolean>(false)

    /**
     * Initialize the OAuth client. This Client will handle any login redirect when in standalone mode.
     * When in Outlook, this client is not used.
     */
    useMount(() => {
        PublicClientApplication.createPublicClientApplication({
            auth: {
                clientId: process.env.BOOTSTRAP_CLIENT_ID,
                authority: 'https://login.microsoftonline.com/common',
                navigateToLoginRequestUrl: false,
                onRedirectNavigate: url => {
                    console.debug(DateTime.now().toUTC().toISO(), 'onRedirectNavigate', url)
                    return true
                },
            },
            cache: {
                cacheLocation: BrowserCacheLocation.LocalStorage,
            },
            system: {
                tokenRenewalOffsetSeconds: 300,
                loggerOptions: {
                    loggerCallback: (level: LogLevel, message: string) => {
                        console.debug(message)
                    },
                    logLevel: Number(process.env.MSAL_LOG_LEVEL),
                    piiLoggingEnabled: asBoolean(process.env.VERBOSE_ERRORS),
                },
            },
        }).then(client => {
            console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: pca init complete`)
            setPca(client)

            client
                .handleRedirectPromise()
                .then((result: AuthenticationResult) => {
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise', JSON.stringify(result))
                    if (result) {
                        console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise setting account')
                        setUser(result.account)
                        parseBootstrapToken(result.accessToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt)
                        setIsUserHydrated(true)
                        setIsBootstrapHydrated(true)
                    } else {
                        const activeAccount = client.getActiveAccount()
                        const currentAccounts = client.getAllAccounts()
                        console.debug(
                            DateTime.now().toUTC().toISO(),
                            'AuthorizationProvider:',
                            `handleRedirectPromise activeAccount=${activeAccount?.username}, numberOfAccounts=${currentAccounts?.length}`,
                        )
                        if (activeAccount) {
                            setUser(activeAccount)
                            setIsUserHydrated(true)
                        } else if (currentAccounts && currentAccounts.length === 1) {
                            setUser(currentAccounts[0])
                            setIsUserHydrated(true)
                        }
                    }
                    setIsPCAReady(true)
                })
                .catch(e => console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: handleRedirectPromise', e))
        })
    })

    /**
     * Add an Async Handler for Outlook which detects if the user switches to a shared mailbox.
     */
    useMount(() => {
        if (isOfficeInitialized()) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider:', Office.context.mailbox.userProfile)

            if (Office.context.mailbox.addHandlerAsync) {
                Office.context.mailbox.addHandlerAsync(
                    Office.EventType.ItemChanged,
                    () => {
                        console.debug(
                            DateTime.now().toUTC().toISO(),
                            'AuthorizationProvider:',
                            'Detected switch to mailbox item:',
                            Office.context.mailbox.item?.subject,
                        )
                        checkSharedMailboxEmail(setMailbox)
                    },
                    result => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            console.debug(
                                DateTime.now().toUTC().toISO(),
                                'AuthorizationProvider:',
                                'Error in ItemChanged handler:',
                                result.error.code,
                                result.error.name,
                                result.error.message,
                            )
                        }
                    },
                )

                return () => Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged)
            } else {
                console.error('file.pivot.document.error.outdated_outlook')
            }
        }

        return undefined
    })

    /**
     * Acquire a bootstrap token. The request is stored in a State to prevent multiple concurrent requests.
     *
     * This function has 2 flows:
     * - Outlook: Only silently request an access token.
     * - Standalone: Attempt to silently acquire a bootstrap token and fall back to a redirect if this fails.
     */
    const acquireBootstrapTokenSilent = (): Promise<void> => {
        if (bootstrapTokenRequestRef.current) {
            return bootstrapTokenRequestRef.current
        }

        let request: Promise<void>
        if (isOfficeInitialized()) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (office) started')
            request = Office.auth
                .getAccessToken({ allowConsentPrompt: true, allowSignInPrompt: true })
                .then(result => parseBootstrapToken(result, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt))
                .catch(error => {
                    console.debug(DateTime.now().toUTC().toISO(), `AuthorizationProvider: getBootstrapToken due to error: ${JSON.stringify(error)}`)
                    setBootstrapTokenExpiresAt(DateTime.now().plus({ minute: 1 }).toUTC())
                })
                .finally(() => {
                    bootstrapTokenRequestRef.current = undefined
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (office) finished')
                })
        } else if (isPCAReady && user) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (silent) started')
            request = pca
                .acquireTokenSilent({
                    authenticationScheme: 'Bearer',
                    account: user,
                    scopes: AUTHENTICATION_SCOPES,
                })
                .then(result => parseBootstrapToken(result.accessToken, user, setUser, bootstrapTokenRef, setBootstrapTokenExpiresAt))
                .catch(error => {
                    console.debug(
                        DateTime.now().toUTC().toISO(),
                        `AuthorizationProvider: getBootstrapToken (silent) due to error: ${JSON.stringify(error)}`,
                    )
                    // Clear the timeout to prevent a racing retry while redirect is in progress
                    if (bootstrapTokenTimeoutRef.current) {
                        clearTimeout(bootstrapTokenTimeoutRef.current)
                        bootstrapTokenTimeoutRef.current = undefined
                    }
                    // Clear the ref so the redirect guard doesn't block, and clear the timeout to prevent a racing retry
                    bootstrapTokenRequestRef.current = undefined
                    return acquireBootstrapTokenRedirect()
                })
                .finally(() => {
                    bootstrapTokenRequestRef.current = undefined
                    console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (silent) finished')
                })
        } else {
            request = acquireBootstrapTokenRedirect()
        }

        bootstrapTokenRequestRef.current = request
        return request
    }

    /**
     * Invoke an HTTP redirect to get a user and bootstrap token.
     */
    const acquireBootstrapTokenRedirect = (): Promise<void> => {
        if (bootstrapTokenRequestRef.current) {
            return bootstrapTokenRequestRef.current
        }

        console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (redirect) started')

        const request: Promise<void> = pca
            .acquireTokenRedirect({
                authenticationScheme: 'Bearer',
                account: user,
                scopes: AUTHENTICATION_SCOPES,
                prompt: pca.getAllAccounts()?.length > 0 ? undefined : 'select_account',
            })
            .finally(() => {
                bootstrapTokenRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: getBootstrapToken (redirect) finished')
            })

        bootstrapTokenRequestRef.current = request
        return request
    }

    /**
     * Fetch available hosts when bootstrapToken is available, but we have not yet set a host.
     *
     */
    const acquireHosts = (): Promise<void> => {
        if (hostsRequestRef.current) {
            return hostsRequestRef.current
        }

        let decodedBootstrapToken = undefined
        try {
            decodedBootstrapToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            'AuthorizationProvider: acquireHosts started',
            host?.RowKey,
            '. bootstrapToken:',
            decodedBootstrapToken?.uti,
        )

        const request: Promise<void> = axios
            .get<Host[]>(`/api/host`, {
                baseURL: process.env.ROUTER_HOST,
                headers: { Authorization: `Bearer ${bootstrapTokenRef.current}`, SuppressErrors: 'true' },
            })
            .then(response => {
                setHosts(response.data)
                if (response.data?.length === 1) {
                    setHost(response.data[0])
                    if (response.data[0].tokens) {
                        setTokens(response.data[0].tokens)
                        setTokensExpiresAt(getTokenExpiryDateTime(response.data[0].tokens?.[SecurityAuthority.WebPubSub]))
                    }
                } else if (!response.data?.some(h => h.RowKey === host?.RowKey)) {
                    setTokens(undefined)
                    setTokensExpiresAt(undefined)
                }
            })
            .catch(error => console.error('AuthorizationProvider: error', error))
            .finally(() => {
                hostsRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: acquireHosts finished')
            })

        hostsRequestRef.current = request
        return request
    }

    const acquireTokens = (): Promise<void | Tokens> => {
        if (tokensRequestRef.current) {
            return tokensRequestRef.current
        }

        let decodedBootstrapToken = undefined
        try {
            decodedBootstrapToken = jwtDecode<OfficeTokenPayload>(bootstrapTokenRef.current)
        } catch (e) {
            console.error(e)
        }
        console.debug(
            DateTime.now().toUTC().toISO(),
            'AuthorizationProvider: acquireTokens started',
            host?.RowKey,
            '. bootstrapToken: ',
            decodedBootstrapToken?.uti,
        )

        const request: Promise<void | Tokens> = axios
            .get<Tokens>(`/api/tokens/${host?.RowKey}`, {
                baseURL: process.env.ROUTER_HOST,
                headers: { Authorization: `Bearer ${bootstrapTokenRef.current}`, SuppressErrors: 'true' },
            })
            .then(response => {
                let decodedToken = undefined
                try {
                    decodedToken = jwtDecode<OfficeTokenPayload>(response.data?.[SecurityAuthority.Hammock]?.access_token)
                } catch (e) {
                    console.error(e)
                }
                console.debug(
                    DateTime.now().toUTC().toISO(),
                    'AuthorizationProvider: acquireTokens. New HAMMOCK token:',
                    decodedToken?.uti,
                    '. Expires at ',
                    getTokenExpiryDateTime(response.data?.[SecurityAuthority.Hammock]).toISO(),
                )
                setTokens(response.data)
                setTokensExpiresAt(getTokenExpiryDateTime(response.data?.[SecurityAuthority.WebPubSub]))
                return response.data
            })
            .catch(error => {
                console.error('AuthorizationProvider: error', error)
                if (isTransientNetworkError(error)) {
                    setTokensExpiresAt(DateTime.now().plus({ seconds: 10 }).toUTC())
                }
            })
            .finally(() => {
                tokensRequestRef.current = undefined
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: acquireTokens finished')
            })

        tokensRequestRef.current = request
        return request
    }

    /**
     * ------------------
     */

    /**
     * Update user, mailbox, bootstrap token, hosts and host with information from the cookies, if present.
     */
    useEffect(() => {
        if (!isPCAReady) {
            return
        }
        if (!loadedFirstVisit || firstVisit) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: not hydrating user because this is the first visit.')
            return
        }

        if (authenticationStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authenticationStorage')
            setUser(user ?? authenticationStorage?.user)
            setMailbox(mailbox ?? authenticationStorage?.mailbox)
        }
        setIsUserHydrated(authenticationStorageLoaded)
    }, [isPCAReady, authenticationStorageLoaded, firstVisit])

    useEffect(() => {
        if (!isUserHydrated) {
            return
        }
        setAuthenticationStorage({ identified, authenticated, user, mailbox })
    }, [isUserHydrated, authenticationStorageLoaded, user, mailbox])

    useEffect(() => {
        if (!isUserHydrated) {
            return
        }
        if (authorizationHostStorageLoaded && !!user) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authorizationHostStorage')
            if (bootstrapTokenRef.current ?? authorizationHostStorage?.bootstrapToken) {
                parseBootstrapToken(
                    bootstrapTokenRef.current ?? authorizationHostStorage?.bootstrapToken,
                    user,
                    setUser,
                    bootstrapTokenRef,
                    setBootstrapTokenExpiresAt,
                )
            }
            setHosts(hosts ?? authorizationHostStorage?.hosts)
            setHost(host ?? authorizationHostStorage?.host)
        }
        setIsBootstrapHydrated(authorizationHostStorageLoaded)
    }, [isUserHydrated, authorizationHostStorageLoaded, user])

    useEffect(() => {
        if (!isBootstrapHydrated) {
            return
        }
        setAuthorizationHostStorage({ bootstrapToken: bootstrapTokenRef.current, hosts, host })
    }, [isBootstrapHydrated, authorizationHostStorageLoaded, bootstrapTokenExpiresAt, hosts, host])

    /**
     * Once the PCA is ready and the cookies are hydrated, hydrate the localstorage.
     */
    useEffect(() => {
        if (!isBootstrapHydrated) {
            return
        }
        if (authorizationTokensStorageLoaded) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: hydrating authorizationTokensStorage')
            setTokens(tokens ?? authorizationTokensStorage?.tokens)
            setTokensExpiresAt(getTokenExpiryDateTime((tokens ?? authorizationTokensStorage?.tokens)?.[SecurityAuthority.WebPubSub]))
        }
        setIsHydrated(authorizationTokensStorageLoaded)
    }, [isBootstrapHydrated, authorizationTokensStorageLoaded])

    useEffect(() => {
        if (!isHydrated) {
            return
        }
        setAuthorizationTokensStorage({ tokens, roles })
    }, [isHydrated, authorizationTokensStorageLoaded, tokens])

    /**
     * ------------------
     * Initialize all necessary fields after the localstorage and cookies have been hydrated.
     */

    /**
     * Check if the user is currently in a shared mailbox.
     */
    useEffect(() => {
        if (isOfficeInitialized()) {
            checkSharedMailboxEmail(setMailbox)
        } else {
            setMailbox(user?.username)
        }
    }, [authenticationStorageLoaded, user])

    /**
     * Acquire a bootstraptoken.
     */
    useEffect(() => {
        if (isBootstrapHydrated && (isOfficeInitialized() || user) && !bootstrapTokenValid) {
            acquireBootstrapTokenSilent()
        } else if (isBootstrapHydrated && !isOfficeInitialized() && !user) {
            acquireBootstrapTokenRedirect()
        }
    }, [isBootstrapHydrated, user, bootstrapTokenValid, loadedFirstVisit, firstVisit])

    /**
     * Once we have a bootstrap token but no host has been selected, fetch available hosts.
     */
    useEffect(() => {
        if (isBootstrapHydrated && bootstrapTokenValid && !host) {
            acquireHosts()
        }
    }, [isBootstrapHydrated, bootstrapTokenValid, host])

    /**
     * Set the expiry timeout for the bootstrap token. Use a minimum of 3 seconds and a maximum of 30 minutes.
     */
    useEffect(() => {
        if (isBootstrapHydrated && bootstrapTokenExpiresAt) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: bootstrapTokenExpiresAt changed to',
                bootstrapTokenExpiresAt.toJSON(),
            )

            const timer = Math.min(Math.max(bootstrapTokenExpiresAt.toMillis() - DateTime.now().toUTC().toMillis(), 3_000), 1_800_000)
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: Setting timeout for bootstrapToken in', timer, 'milliseconds.')

            if (bootstrapTokenTimeoutRef.current) {
                clearTimeout(bootstrapTokenTimeoutRef.current)
            }
            bootstrapTokenTimeoutRef.current = setTimeout(() => acquireBootstrapTokenSilent(), timer)
            return () => {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing timeout for bootstrapToken')
                if (bootstrapTokenTimeoutRef.current) {
                    clearTimeout(bootstrapTokenTimeoutRef.current)
                    bootstrapTokenTimeoutRef.current = undefined
                }
            }
        } else if (bootstrapTokenTimeoutRef.current) {
            console.debug(
                DateTime.now().toUTC().toISO(),
                'AuthorizationProvider: bootstrapTokenExpiresAt changed to undefined, clearing timeout for bootstrapToken',
            )
            clearTimeout(bootstrapTokenTimeoutRef.current)
            bootstrapTokenTimeoutRef.current = undefined
        }
        return () => {}
    }, [isBootstrapHydrated, bootstrapTokenExpiresAt])

    /**
     * Fetch the tokens when we have a bootstrap token but no valid pub sub token.
     */
    useEffect(() => {
        if (isHydrated && bootstrapTokenValid && host && !isTokenNotExpired(tokens?.[SecurityAuthority.WebPubSub])) {
            acquireTokens()
        } else if (isHydrated && !bootstrapTokenValid) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: bootstrapToken is undefined')
        } else if (isHydrated && !host) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: host is undefined')
        }
    }, [isHydrated, bootstrapTokenValid, host])

    /**
     * Set the expiry timeout for the tokens. Use a minimum of 3 seconds and a maximum of 25 minutes.
     */
    useEffect(() => {
        if (isHydrated && tokensExpiresAt) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: tokensExpiresAt changed to', tokensExpiresAt.toJSON())

            const timer = Math.min(Math.max(tokensExpiresAt.toMillis() - DateTime.now().toUTC().toMillis(), 3_000), 1_500_000)
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: Setting timeout for tokens in', timer, 'milliseconds.')

            if (tokensTimeoutRef.current) {
                clearTimeout(tokensTimeoutRef.current)
            }
            tokensTimeoutRef.current = setTimeout(() => (host ? acquireTokens() : Promise.resolve()), timer)
            return () => {
                console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: clearing timeout for tokens')
                if (tokensTimeoutRef.current) {
                    clearTimeout(tokensTimeoutRef.current)
                    tokensTimeoutRef.current = undefined
                }
            }
        } else if (tokensTimeoutRef.current) {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: tokensExpiresAt changed to undefined, clearing timeout for tokens')
            clearTimeout(tokensTimeoutRef.current)
            tokensTimeoutRef.current = undefined
        }
        return () => {}
    }, [isHydrated, host, tokensExpiresAt])

    useEffect(() => {
        window.addEventListener('offline', () => {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: window went offline')
            clearTimeout(tokensTimeoutRef.current)
            clearTimeout(bootstrapTokenTimeoutRef.current)
        })

        window.addEventListener('online', () => {
            console.debug(DateTime.now().toUTC().toISO(), 'AuthorizationProvider: window went online')
            setTokensExpiresAt(tokensExpiresAt)
            setBootstrapTokenExpiresAt(bootstrapTokenExpiresAt)
        })
    }, [])

    const updateTokens = (authority: SecurityAuthority): Promise<ServerAuthorizationTokenResponse | WebPubSubToken> => {
        let request = Promise.resolve()

        if (!bootstrapTokenExpiresAt || bootstrapTokenExpiresAt?.diff(DateTime.now()).toObject().milliseconds <= 0) {
            request = request.then(() => acquireBootstrapTokenSilent())
        }
        if (!hosts || !host) {
            request = request.then(() => acquireHosts())
        }

        return request.then(() => (host ? acquireTokens() : Promise.resolve())).then(tokens => tokens?.[authority])
    }

    const getBaseUrl = (destination: Destination = Destination.Hammock): string | undefined => {
        if (!host) {
            return undefined
        }

        switch (destination) {
            case Destination.SharePoint:
            case Destination.Exchange:
                return graphBaseUrl
            case Destination.Documents:
                return `${graphBaseUrl}/drives/${host?.DriveId}`
            case Destination.ReplicationEmail:
                return `${graphBaseUrl}/users/${host?.ReplicationMail}`
            case Destination.Email:
                return `${graphBaseUrl}/${mailbox ? 'users/' + mailbox : 'me'}`
            case Destination.PrivateEmail:
                return `${graphBaseUrl}/me`
            case Destination.Hammock:
            default:
                return host?.Backend
        }
    }

    const resetUser = () => {
        //Reset both to force both Standalone and Outlook to refresh user and bootstrap token.
        setUser(undefined)
        bootstrapTokenRef.current = undefined
        setHost(undefined)
        setAuthenticationStorage(undefined)
        setAuthorizationHostStorage(undefined)
        pca.clearCache({ account: user }).then(() => pca.setActiveAccount(null))
    }

    return (
        <AuthorizationContext.Provider
            value={{
                identified,
                authenticated,
                user,
                mailbox,
                bootstrapToken: bootstrapTokenRef.current,
                hosts,
                host,
                tokens,
                roles,
                updateTokens,
                setHost: (host: Host) => {
                    if (host !== undefined) {
                        setHost(host)
                    }
                },
                getBaseUrl,
                reset: resetUser,
            }}
        >
            {props.children}
        </AuthorizationContext.Provider>
    )
}

// noinspection JSUnusedGlobalSymbols
export default AuthorizationContext
